// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Download, ListChecks, AlertTriangle, Check } from 'lucide-react';
import { getPunchList } from '../api/workbenchApi';
import RefreshSection from '../components/RefreshSection';

const fmtUSD = (v) => {
  const n = Number(v || 0);
  if (n === 0) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
};

const STATUS_COLOUR = {
  needs_input: '#f59e0b',
  proposed: '#3b82f6',
  approved: '#22c55e',
};

const buildMarkdown = (data, sessionName) => {
  const lines = [];
  lines.push(`# Diligence Punch List${sessionName ? ` — ${sessionName}` : ''}`);
  lines.push('');
  lines.push(`_${data.total_questions} open question${data.total_questions === 1 ? '' : 's'}_`);
  lines.push('');
  for (const theme of data.themes || []) {
    lines.push(`## ${theme.label} (${theme.count})`);
    lines.push('');
    theme.items.forEach((item, idx) => {
      const impact = Number(item.annual_impact || 0);
      const impactStr = impact !== 0 ? ` _(annual impact: ${fmtUSD(impact)})_` : '';
      lines.push(`${idx + 1}. **${item.source_account_name}**${impactStr}`);
      lines.push(`   ${item.question}`);
      if (item.analyst_note) {
        lines.push(`   > Analyst note: ${item.analyst_note}`);
      }
      lines.push('');
    });
  }
  return lines.join('\n');
};

const PunchList = ({ mappingSessionId, sessionName, bootstrap, onOpenDrilldown }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const load = useCallback(() => {
    if (!mappingSessionId) return () => {};
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPunchList(mappingSessionId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mappingSessionId]);

  useEffect(() => load(), [load, bootstrap]);

  const markdown = useMemo(() => (data ? buildMarkdown(data, sessionName) : ''), [data, sessionName]);

  const copyOne = async (item) => {
    try {
      await navigator.clipboard.writeText(`${item.source_account_name}\n${item.question}`);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1200);
    } catch (e) {
      // No clipboard permission — surface a tiny inline error.
      setCopiedId(`err:${item.id}`);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1500);
    } catch (e) {
      setCopiedAll(false);
    }
  };

  const downloadMarkdown = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().split('T')[0];
    a.download = `qoe-punch-list-${stamp}.md`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (loading && !data) {
    return (
      <div>
        <div className="qoe-wb-section-header">
          <h2>
            Diligence Punch List
            <RefreshSection onRefresh={load} busy={loading} label="Refresh punch list" />
          </h2>
        </div>
        <div className="qoe-wb-loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div>
        <div className="qoe-wb-section-header">
          <h2>
            Diligence Punch List
            <RefreshSection onRefresh={load} busy={loading} label="Refresh punch list" />
          </h2>
        </div>
        <div className="qoe-wb-row-error fail" style={{ maxWidth: 'unset' }}>
          {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  if (data.total_questions === 0) {
    return (
      <div>
        <div className="qoe-wb-section-header">
          <h2>
            Diligence Punch List
            <RefreshSection onRefresh={load} busy={loading} label="Refresh punch list" />
          </h2>
        </div>
        <div className="qoe-wb-conc-empty">
          Nothing to ask the seller yet — every flagged adjustment has been rejected
          or there are no flags on this engagement.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="qoe-wb-section-header">
        <h2>Diligence Punch List</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="qoe-wb-btn ghost" onClick={copyAll}>
            {copiedAll ? <Check size={12} /> : <Copy size={12} />}
            {copiedAll ? ' Copied' : ' Copy Markdown'}
          </button>
          <button className="qoe-wb-btn" onClick={downloadMarkdown}>
            <Download size={12} /> Download .md
          </button>
        </div>
      </div>

      <div className="qoe-wb-punch-summary">
        <div>
          <ListChecks size={14} />
          <strong>{data.total_questions}</strong> open question
          {data.total_questions === 1 ? '' : 's'}
        </div>
        {data.counts_by_status?.needs_input > 0 && (
          <div className="warn">
            <AlertTriangle size={14} />
            <strong>{data.counts_by_status.needs_input}</strong> need analyst input first
          </div>
        )}
      </div>

      {data.themes.map((theme) => (
        <div key={theme.key} className="qoe-wb-punch-theme">
          <div className="qoe-wb-punch-theme-header">
            <h3>{theme.label}</h3>
            <span className="qoe-wb-section-meta">
              {theme.count} item{theme.count === 1 ? '' : 's'}
            </span>
          </div>
          <ol className="qoe-wb-punch-list">
            {theme.items.map((item) => (
              <li key={item.id} className="qoe-wb-punch-item">
                <div className="qoe-wb-punch-item-head">
                  <span
                    className="qoe-wb-punch-status"
                    style={{ background: STATUS_COLOUR[item.status] || '#94a3b8' }}
                  />
                  <button
                    className="qoe-wb-account-link"
                    onClick={() => onOpenDrilldown && onOpenDrilldown(item.source_account_name)}
                    disabled={!onOpenDrilldown}
                  >
                    {item.source_account_name}
                  </button>
                  {Number(item.annual_impact || 0) !== 0 && (
                    <span className="qoe-wb-punch-impact">
                      {fmtUSD(item.annual_impact)}
                    </span>
                  )}
                  <button
                    className="qoe-wb-punch-copy"
                    onClick={() => copyOne(item)}
                    title="Copy this question to clipboard"
                  >
                    {copiedId === item.id ? <Check size={12} /> : <Copy size={12} />}
                  </button>
                </div>
                <p className="qoe-wb-punch-question">{item.question}</p>
                {item.analyst_note && (
                  <div className="qoe-wb-punch-note">Analyst note: {item.analyst_note}</div>
                )}
                {item.status === 'needs_input' && item.llm_input_needed?.length > 0 && (
                  <div className="qoe-wb-punch-needed">
                    Needs: {item.llm_input_needed.map((f) => f.replace(/_/g, ' ')).join(', ')}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
};

export default PunchList;
